import { ClockIcon, MailIcon, XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";

import RevokeInvitationForm from "@/features/members/components/RevokeInvitationForm";
import { RoleBadge } from "@/features/members/components/RoleBadge";
import { ProjectInvitation, ProjectRoleType } from "@/features/projects/types";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";

interface Props {
  invitation: ProjectInvitation;
  projectId: string;
  canRevokeInvitations: boolean;
}

export function InvitationRow({ invitation, projectId, canRevokeInvitations }: Props) {
  const invitedAt = invitation.invitedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const invitedByName = `${invitation.invitedBy.firstName} ${invitation.invitedBy.lastName}`;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2.5">
          <div className={`flex size-8 shrink-0 items-center justify-center ${invitation.isExpired ? "bg-muted/50 text-muted-foreground/50" : "bg-muted text-muted-foreground"}`}>
            <MailIcon className="size-3.5" />
          </div>
          <div>
            <p className={`text-sm font-medium ${invitation.isExpired ? "text-muted-foreground" : ""}`}>{invitation.emailAddress}</p>
            <p className="text-xs text-muted-foreground">{invitation.isExpired ? "Invite expired" : "Invite pending"}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <RoleBadge role={invitation.role as ProjectRoleType} />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{invitedByName}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{invitedAt}</TableCell>
      <TableCell>
        {invitation.isExpired ? (
          <Badge variant="secondary" className="gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <ClockIcon className="size-2.5" />
            Expired
          </Badge>
        ) : (
          <PermissionTooltip allowed={canRevokeInvitations} message="You don't have permission to revoke invitations.">
            {canRevokeInvitations ? (
              <RevokeInvitationForm projectId={projectId} invitation={invitation} />
            ) : (
              <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive">
                <XIcon className="size-3.5" />
              </Button>
            )}
          </PermissionTooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
